/**
 * Фикс судна в карточке: ГДЕ он снят и КОГДА — двумя готовыми подписями.
 *
 * До 21.08.2026 карточка отвечала на второй вопрос словом «сейчас» («младше
 * часа»), а на первый не отвечала вовсе. Этого хватает, пока верят на слово, и
 * не хватает ровно в тот момент, когда рядом открыт второй источник: сверить
 * два числа можно, лишь имея число от каждого. Разбор того дня (LEDA777,
 * MMSI 273210650) кончился тем, что замер оказался у MarineTraffic — судно
 * вышло из зоны берегового приёма, и его точка стояла семь часов, — но узнать
 * это удалось только запросом в базу.
 *
 * Обе подписи ЛИБО есть, ЛИБО их нет: прочерк на месте координаты читается как
 * «судно есть, места нет», а такого судна на карте не бывает — слой отбирает
 * фиксы с точкой (`last_lat IS NOT NULL`). Прочерк вместо времени хуже вдвойне:
 * он утверждает, что время спрашивали.
 */
import { coordText } from '../map/pointCardView'
import { formatDayTime, msg } from '../i18n'
import type { Lang, Msg } from '../i18n'

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * «54,632° с.ш.  162,949° в.д.» — координаты фикса словами.
 *
 * Читаются из СВОЙСТВ фичи, а не из её геометрии и не из места отрисовки
 * карточки: геометрия принадлежит карте и приводится к обороту мира кадра (см.
 * `anchorLng`), а здесь печатается измерение из `ais_vessel`. Само написание —
 * общее с отсчётом в точке (`coordText`): одна карта не может называть одно и
 * то же место двумя способами.
 */
export function fixCoords(props: Record<string, unknown>, lang: Lang): string | null {
  const lat = num(props.lat)
  const lon = num(props.lon)
  if (lat === null || lon === null) return null
  return coordText(lat, lon, lang)
}

/**
 * Когда фикс снят: «21.08.2026, 09:28 UTC».
 *
 * Отдаёт `Msg`, а не фразу: пояс — слово каталога («Время (UTC)» уже живёт там
 * у землетрясений), и вторая его копия в коде разъехалась бы молча. Сам пояс
 * назван вслух не по привычке: выгрузка кабинета ShipFinder живёт в UTC+8, и
 * «позиции из будущего» мы уже ловили — метка без пояса сдвинула бы сверку с
 * чужим источником ровно на восемь часов, оставшись правдоподобной.
 */
export function fixTime(iso: string | null | undefined, lang: Lang): Msg | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null
  const time = formatDayTime(lang, iso)
  // `formatDayTime` возвращает вход как есть, когда он не разбирается в дату
  // (поломку она не прячет). Здесь это значит «времени нет»: подписать мусор
  // поясом — выдать его за время.
  if (time === iso) return null
  return msg('models.vessel.fixAt', { time })
}
